import React from 'react';
import {
  AppBar as MuiAppBar,
  AppBarProps as MuiAppbarProps,
  styled,
  Toolbar as MuiToolbar,
  Link,
} from '@mui/material';
import { Breadcrumbs } from '../breadcrumbs';
import HomeLink, { HomeLinkProps } from './HomeLink';
import Navigation, { NavigationProps, NavigationTabProps } from './Navigation';
import { SearchField, SearchFieldProps } from '../searchField';
import AppsList, { AppsListProps } from './AppsList';
import NewMenu, { NewMenuProps } from './NewMenu';
import UserMenu, { UserMenuProps } from './UserMenu';

export interface AppBarProps extends Omit<MuiAppbarProps, 'elevation' | 'variant' | 'color'> {
  homeIcon?: HomeLinkProps['Icon'],
  homeLabel?: HomeLinkProps['label'],
  homeHref?: HomeLinkProps['href'],

  navigationTabs: NavigationProps['tabs'],
  navigationActiveTabIndex?: number;

  newMenuActions?: NewMenuProps['actions'],

  onSearchSubmit?: SearchFieldProps['onSubmit'],
  onDrawOpen?: SearchFieldProps['onDrawOpen'],

  userInitials: string;
  userMenuActions: UserMenuProps['actions'],
  userMenuAvatarColor?: UserMenuProps['color'],

  utilities?: React.ReactNode,

  apps?: AppsListProps['apps'],

  secondaryVariant?: boolean,

  breadcrumbs?: NavigationTabProps[],
}

export const AppBarHeight = 52;

export const AppBarRight = styled('div')(() => ({
  display: 'flex',
  height: AppBarHeight,
  marginLeft: 'auto',
  alignItems: 'center',
}));

function AppBar({
  homeIcon, homeLabel, homeHref,
  navigationTabs, navigationActiveTabIndex,
  newMenuActions,
  onSearchSubmit, onDrawOpen,
  userInitials, userMenuActions, userMenuAvatarColor = 'brand4',
  apps, utilities, secondaryVariant, breadcrumbs,
  ...props
}: AppBarProps) {
  return (
    <MuiAppBar {...props}>
      <MuiToolbar>
        <HomeLink
          Icon={homeIcon}
          label={homeLabel}
          href={homeHref}
        />

        <Navigation
          secondaryVariant={secondaryVariant}
          tabs={navigationTabs}
          activeIndex={navigationActiveTabIndex}
        />

        {secondaryVariant && (
          <Breadcrumbs iconSeparator aria-label="breadcrumbs">
            {breadcrumbs?.map((breadcrumb) => (
              <Link key={breadcrumb.href} href={breadcrumb.href}>
                <span>{breadcrumb.label}</span>
              </Link>
            ))}
          </Breadcrumbs>
        )}

        {newMenuActions
          && <NewMenu actions={newMenuActions} />}

        <AppBarRight>
          {onSearchSubmit
            && (
              <SearchField
                onSubmit={onSearchSubmit}
                onDrawOpen={onDrawOpen}
              />
            )}

          {utilities}

          <UserMenu
            userInitials={userInitials}
            actions={userMenuActions}
            color={userMenuAvatarColor}
          />

          {apps && <AppsList apps={apps} />}
        </AppBarRight>
      </MuiToolbar>
    </MuiAppBar>
  );
}

export default React.memo(AppBar);
